import * as R from 'ramda';

import { countryName } from '../services/country';
import { parsedUrl } from '../services/url';
import { countryCode, language } from '../services/translator';

import { BOOXI_QUERY_PARAMETER } from '../settings/booxi';
import {
  POS_FILTERS_QUERY_PARAMETER,
  POS_SEARCH_QUERY_PARAMETER,
  POS_SELECTED_ZOOM_LEVEL,
  POS_SHOP_QUERY_PARAMETER,
  POS_ZOOM_LEVEL
} from '../settings/pointOfSale';
import {
  CHANGE_TRAVEL_MODE,
  CLOSE_ANNOUNCEMENT,
  CLOSE_ITINERARY_DETAILS,
  CLOSE_MOBILE_SHOP,
  FETCH_ITINERARY,
  MAP_IS_READY,
  MAP_SCRIPT_LOADED,
  MARKERS_UPDATED,
  OPEN_ITINERARY_DETAILS,
  RECEIVE_ANNOUNCEMENT,
  RECEIVE_POINTS_OF_SALE,
  RECEIVE_SEARCH_RESULTS,
  RECEIVE_FILTERS,
  SEARCH,
  SELECT_POINTS_OF_SALE,
  SELECT_POS_AND_CLOSE_ANNOUNCEMENT,
  SET_CURRENT_ADDRESS,
  SET_PANEL,
  TOGGLE_CATEGORY_FILTER,
  TOGGLE_ITINERARY,
  UPDATE_FILTERS,
  VISIBLE_MARKERS_UPDATED,
  ZOOM_CHANGED
} from '../actions/pointOfSale';
import { SUBMIT_POPIN_APPOINTMENT, SUBMIT_POPIN_SHARE } from '../actions/popin';
import { getActiveMenu } from '../actions/nav';
import { TOGGLE_POPIN_APPOINTMENT, TOGGLE_POPIN_SHARE } from '../actions/popin';
import { TRAVEL_MODES } from 'ui-kit/src/components/Itinerary/Itinerary';
import { isGMap } from 'ui-kit/src/shared/mapApi';
import { trackAppointment, trackFilter, trackItinerary, trackSearch, trackShare } from '../services/gtm';
import replaceLang from 'ui-kit/src/shared/replaceLang';
import { INTEGRATION_MODE_QUERY_PARAMETER } from '../settings/shared';

export const MAP_NOT_READY = 0;
export const MAP_READY = 1;

let activeMenu, filtersSelected, searchValue;
let booxiId = parsedUrl.query[BOOXI_QUERY_PARAMETER];
let selectedId = parsedUrl.query[POS_SHOP_QUERY_PARAMETER] || booxiId;
let integrationMode = parsedUrl.query[INTEGRATION_MODE_QUERY_PARAMETER];
if (!selectedId ) {
  activeMenu = getActiveMenu();
  filtersSelected = parsedUrl.query[POS_FILTERS_QUERY_PARAMETER];
  searchValue = parsedUrl.query[POS_SEARCH_QUERY_PARAMETER];
}

const initialState = {
  allItems: [],
  allSearchResults: [],
  api: null,
  announcement: null,
  categoryOpen: null,
  currentAddress: '',
  items: [],
  itinerary: null,
  isItineraryVisible: false,
  isItineraryDetailsOpen: false,
  isMapReady: MAP_NOT_READY,
  isMapScriptLoaded: false,
  isMobileShopOpen: false,
  fieldValue: selectedId ? null : searchValue,
  filters: {},
  filtersSelected: filtersSelected ? filtersSelected.split(',') : [],
  map: null,
  markers: [],
  panel: null,
  searchCount: 0,
  searchPosition: null,
  searchResults: [],
  searchResultsViewport: null,
  searchViewport: null,
  searchValue: selectedId ? null : (searchValue || countryName),
  selectedId: null,
  selectedItem: null,
  travelMode: Object.keys(TRAVEL_MODES)[0],
  visibleMarkerIds: [],
  zoom: 2
};

export function pointOfSale(state = initialState, action) {
  let selectedItem, searchResults;

  switch (action.type) {
    case CHANGE_TRAVEL_MODE:
      return {
        ...state,
        travelMode: action.data
      };

    case CLOSE_ANNOUNCEMENT:
      return {
        ...state,
        announcement: null
      };

    case CLOSE_ITINERARY_DETAILS:
      return {
        ...state,
        isItineraryDetailsOpen: false,
        itinerary: null
      };

    case CLOSE_MOBILE_SHOP:
      return {
        ...state,
        isMobileShopOpen: false,
        selectedId: null,
        selectedItem: null
      };

    case FETCH_ITINERARY:
      trackItinerary(state.selectedItem.defaultName, state.travelMode);
      return {
        ...state,
        itinerary: action.data,
        isItineraryDetailsOpen: true
      };

    case MAP_IS_READY:
      return {
        ...state,
        isMapReady: MAP_READY,
        map: action.map
      };

    case MAP_SCRIPT_LOADED:
      return {
        ...state,
        api: action.api,
        isMapScriptLoaded: true
      };

    case MARKERS_UPDATED:
      return {
        ...state,
        markers: action.markers,
        searchResultsViewport: action.bounds,
        searchViewport: null,
        zoom: action.zoom
      };

    case OPEN_ITINERARY_DETAILS:
      return {
        ...state,
        isItineraryDetailsOpen: true
      };

    case RECEIVE_ANNOUNCEMENT:
      return {
        ...state,
        announcement: action.data
      };

    case RECEIVE_POINTS_OF_SALE:
      if (selectedId && R.find(R.propEq(0, selectedId))(action.data.items)) {
        state.searchViewport = null;
        state.selectedId = selectedId;
        state.zoom = POS_SELECTED_ZOOM_LEVEL;
        selectedId = null;
      }
      return {
        ...state,
        allItems: action.data.items,
        items: filterItems(state.filtersSelected, action.data.items)
      };

    case RECEIVE_SEARCH_RESULTS:
      if (state.searchPosition) {
        let lat, lng;
        if (isGMap(state.api)) {
          lat = state.searchPosition.lat();
          lng = state.searchPosition.lng();
        } else {
          lat = state.searchPosition.lat;
          lng = state.searchPosition.lng;
        }
        action.data.Items.forEach(item => item.distance = distanceInKm(item.lat, item.lng, lat, lng));
        action.data.Items.sort(sortByDistance);
      }
      selectedItem = getShop(state.selectedId, action.data.Items);
      searchResults = filterSearchResults(state.filters, action.data.Items, state.visibleMarkerIds);

      return {
        ...state,
        allSearchResults: action.data.Items,
        searchCount: searchResults.length,
        searchResults,
        selectedId: selectedItem ? selectedItem.id : null,
        isMobileShopOpen: !!selectedItem,
        selectedItem
      };

    case RECEIVE_FILTERS:
      if (filtersSelected) {
        action.data.items
          .forEach((univers, index) => {
            if (univers.subUniverses.filter(subUnivers => subUnivers.id === state.filtersSelected[0]).length) {
              state.categoryOpen = index;
            }
          });
      } else if (activeMenu && !integrationMode) {
        const universe = R.find(filter =>
          filter.diorRef && filter.diorRef.split(',').indexOf(activeMenu) !== -1
        )(action.data.items);
        if (universe) {
          state.categoryOpen = action.data.items.indexOf(universe);
          filtersSelected = universe.subUniverses.map(subUniverse => subUniverse.id);
          state = {...state, ...updateFiltersState(state, filtersSelected)};
        }
      }
      filtersSelected = null;
      return {
        ...state,
        filters: action.data
      };

    case SEARCH:
      if (action.userAction) {
        trackSearch(state.searchValue);
      }
      if (action.viewport) {
        return {
          ...state,
          searchViewport: action.viewport,
          zoom: null
        };
      } else {
        return {
          ...state,
          searchViewport: action.viewport,
          searchPosition: action.position,
          zoom: POS_ZOOM_LEVEL
        };
      }

    case SELECT_POINTS_OF_SALE:
      return {
        ...state,
        isMobileShopOpen: true,
        searchViewport: null,
        selectedId: action.data,
        selectedItem: getShop(action.data, state.searchResults),
        zoom: state.zoom < POS_ZOOM_LEVEL ? POS_ZOOM_LEVEL : state.zoom
      };

    case SELECT_POS_AND_CLOSE_ANNOUNCEMENT:
      return {
        ...state,
        announcement: null,
        searchViewport: null,
        selectedId: action.data,
        selectedItem: getShop(action.data, state.searchResults),
        zoom: state.zoom < POS_ZOOM_LEVEL ? POS_ZOOM_LEVEL : state.zoom
      };

    case SET_CURRENT_ADDRESS:
      return {
        ...state,
        currentAddress: action.data,
        fieldValue: action.data,
        searchValue: action.data
      };

    case SET_PANEL:
      return {
        ...state,
        panel: action.panel
      };

    case SUBMIT_POPIN_APPOINTMENT:
      selectedItem = getShop(action.form.posId, state.searchResults);
      trackAppointment(selectedItem.defaultName);
      return state;

    case SUBMIT_POPIN_SHARE:
      selectedItem = getShop(action.form.posId, state.searchResults);
      trackShare(selectedItem.defaultName);
      return state;

    case TOGGLE_CATEGORY_FILTER:
      return {
        ...state,
        categoryOpen: action.data
      };

    case TOGGLE_ITINERARY:
      return {
        ...state,
        isItineraryVisible: action.state,
        itinerary: action.state ? state.itinerary : null,
        selectedId: action.id,
        selectedItem: getShop(action.id, state.searchResults)
      };

    case TOGGLE_POPIN_APPOINTMENT:
    case TOGGLE_POPIN_SHARE:
      if (action.id) {
        return {
          ...state,
          selectedId: action.id,
          selectedItem: getShop(action.id, state.searchResults)
        };
      }
      return state;

    case VISIBLE_MARKERS_UPDATED: {
      const searchResults = filterSearchResults(state.filters, state.allSearchResults, action.markers);
      return {
        ...state,
        searchResults,
        searchCount: searchResults.length,
        visibleMarkerIds: action.markers
      };
    }

    case UPDATE_FILTERS:
      const newState = updateFiltersState(state, action.data);
      if (newState.filtersSelected.length > 0 && R.intersection(state.filtersSelected, newState.filtersSelected).length === 0) {
        // Universe on.
        const universe = state.filters.items
          .find(universe => universe.subUniverses.find(subUniverse => subUniverse.id === newState.filtersSelected[0]));
        trackFilter(replaceLang(universe.localeLabels, universe.defaultLabel, countryCode, language));
      } else if (newState.filtersSelected.length > state.filtersSelected.length) {
        // Sub-universe on
        const selectedFilter = R.difference(newState.filtersSelected, state.filtersSelected)[0];
        const subUniverse = state.filters.items
          .reduce((prevUniverse, universe) => {
            const subUniverse = universe.subUniverses.find(subUniverse => subUniverse.id === selectedFilter);
            return subUniverse ? subUniverse : prevUniverse;
          }, null);
        trackFilter(replaceLang(subUniverse.localeLabels, subUniverse.defaultLabel, countryCode, language));
      }
      return {
        ...state,
        ...newState,
        categoryOpen: newState.filtersSelected.length > 0 ? state.categoryOpen : null
      };

    case ZOOM_CHANGED:
      return {
        ...state,
        zoom: action.zoom
      };

    default:
      return state;
  }
}

function degreesToRadians(degrees) {
  return degrees * Math.PI / 180;
}

function distanceInKm(lat1, lng1, lat2, lng2) {
  const earthRadiusKm = 6371;

  const dLat = degreesToRadians(lat2 - lat1);
  const dLng = degreesToRadians(lng2 - lng1);

  const radLat1 = degreesToRadians(lat1);
  const radLat2 = degreesToRadians(lat2);

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.sin(dLng/2) * Math.sin(dLng/2) * Math.cos(radLat1) * Math.cos(radLat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return Math.abs(earthRadiusKm * c);
}

function sortByDistance(a, b) {
  return a.distance - b.distance;
}

function filterItems(filters, items) {
  if (filters && filters.length > 0) {
    return items
      .filter(item => item[3] && item[3].length > 0 && R.intersection(item[3], filters).length > 0);
  }
  return items;
}

function filterSearchResults(filters, items, visibleMarkerIds) {
  if (filters && filters.length > 0) {
    items = items
      .filter(item => {
        const subUniverses = item.subUniverses.map(subUniverse => subUniverse.id);
        return R.intersection(subUniverses, filters).length > 0
      });
  }
  return items.filter(searchResult => visibleMarkerIds.indexOf(searchResult.id) !== -1);
}

function getShop(id, shops) {
  return R.find(R.propEq('id', id))(shops);
}

function updateFiltersState(state, filters) {
  let selectedItem;
  let searchResults = [];
  if (state.selectedId && state.items) {
    selectedItem = getShop(state.selectedId, state.items);
  }
  if (filters && state.allSearchResults) {
    searchResults = filterSearchResults(filters, state.allSearchResults, state.visibleMarkerIds);
  }
  return {
    filtersSelected: filters,
    items: filterItems(filters, state.allItems),
    isMobileShopOpen: !!selectedItem,
    searchCount: searchResults.length,
    searchResults,
    selectedId: selectedItem ? selectedItem.id : null,
    selectedItem
  };
}



// WEBPACK FOOTER //
// ./src/reducers/pointOfSale.js